import { fmtDate } from "@/lib/utils";
import { Outlet, NavLink, useNavigate, useLocation, Navigate } from "react-router-dom";
import { Suspense } from "react";
import { PageLoader } from "@/components/PageLoader";
import { useEffect, useState } from "react";
import {
    AlertTriangle, Users, LogOut, Building2,
    BarChart3, Calendar, CalendarDays, CalendarClock, Stethoscope, Menu, X, Receipt, HeartPulse, FileText,
    TrendingUp, Settings, BookOpen, ChevronDown, ChevronRight, KeyRound, ShieldAlert, Pill, ShieldCheck, MessageSquare, LifeBuoy, ClipboardList, ClipboardCheck, Rocket, Lock, Scale, Radio, Flag, Hand, FileWarning, CalendarOff, BedDouble, BellRing, ArrowLeftRight, UtensilsCrossed, Accessibility, Skull, FileStack, Gavel, Syringe,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PhoenixWordmark } from "@/components/PhoenixLogo";
import { TIDS } from "@/constants/testIds";
import { api } from "@/lib/api";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";
import { SubscriptionLockScreen } from "@/components/SubscriptionLock";
import { ContractSigningGate } from "@/components/ContractSigningGate";
import { StaffConsentGate } from "@/components/StaffConsentGate";
import { InstitutionGuideModal } from "@/components/InstitutionGuideModal";
import { PharmacyRejectionBanner } from "@/components/PharmacyRejectionBanner";
import { OverduePrecautionBanner } from "@/components/OverduePrecautionBanner";
import { FacilityNotificationBell } from "@/components/FacilityNotificationBell";
import { MyAssignmentPanel } from "@/components/staff/MyAssignmentWidget";
import { LoginAssignmentPopup } from "@/components/staff/LoginAssignmentPopup";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { hasPerm, hasAnyPerm } from "@/lib/permissions";
import { hasPrerelease, hasClinical, hasMedicaid } from "@/lib/tiers";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
    DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
    DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent, DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";

// group "main" → left sidebar (day-to-day ops); group "staff" → user-menu dropdown (supervisor)
const navItems = [
    // Quick access — always visible at the top (Appointments drives the Daily Operations hub)
    { to: "/institution", icon: CalendarDays, label: "Dashboard", tid: TIDS.instNavDashboard, exact: true, group: "main", tabKey: "appointments" },
    { to: "/institution/crisis-dashboard", icon: ShieldAlert, label: "Crisis Dashboard", tid: "inst-nav-crisis-dashboard", group: "main" },
    { to: "/institution/clinical-alerts", icon: BellRing, label: "Clinical Alerts", tid: "inst-nav-clinical-alerts", group: "main" },
    { to: "/institution/auto-scheduling", icon: CalendarClock, label: "Auto-Scheduling", tid: "inst-nav-auto-scheduling", group: "main" },
    // Custody — correctional-officer facing (custody-clinical bridge). Consolidated into one
    // tabbed workspace (Board · Documentation Log · Security Rounds · Safety Checks · Meal Log · Shift Handoff · Contraband).
    { to: "/institution/custody", icon: ClipboardList, label: "Custody", tid: "inst-nav-custody-group", group: "main" },
    // Collapsible group — Clinical Care
    {
        label: "Clinical Care", icon: HeartPulse, group: "main", tid: "inst-nav-clinical-care-group",
        children: [
            { to: "/institution/care-management", label: "Clinical Record", tid: "inst-nav-care-mgmt" },
            { to: "/institution/dental", label: "Dental", tid: "inst-nav-dental" },
            { to: "/institution/scheduling-worklist", label: "Scheduling Worklist", tid: "inst-nav-scheduling-worklist" },
            // Nested sub-group — Programs
            {
                label: "Programs", tid: "inst-nav-programs-group",
                children: [
                    { to: "/institution/withdrawal-monitoring", label: "Withdrawal Monitoring", tid: "inst-nav-withdrawal" },
                    { to: "/institution/mat", label: "MAT / MOUD", tid: "inst-nav-mat" },
                    { to: "/institution/chronic", label: "Chronic Care", tid: "inst-nav-chronic" },
                    { to: "/institution/womens-health", label: "Women's Health", tid: "inst-nav-womens-health" },
                    { to: "/institution/infection-control", label: "Infection Control", tid: "inst-nav-infection-control" },
                ],
            },
            { to: "/institution/med-safety", label: "Medication Safety", tid: "inst-nav-med-safety" },
            { to: "/institution/refusals", label: "Refusal Register", tid: "inst-nav-refusals" },
            { to: "/institution/app-engagement", label: "App Engagement", tid: "inst-nav-app-engagement" },
            { to: "/institution/risk-assessment", label: "Risk Assessment", tid: "inst-nav-risk-assessment" },
            { to: "/institution/recidivism", label: "Recidivism Risk", tid: "inst-nav-recidivism" },
        ],
    },
    // Standalone — Diet Orders / Kitchen (below Clinical Care for quick staff access)
    { to: "/institution/diet", icon: UtensilsCrossed, label: "Diet Orders / Kitchen", tid: "inst-nav-diet", group: "main" },
    // Standalone — Disciplinary Hearings (write-up → hearing → sanction → appeal pipeline)
    { to: "/institution/disciplinary", icon: Gavel, label: "Disciplinary Hearings", tid: "inst-nav-disciplinary", group: "main" },
    // Standalone — ADA Accommodations (active-accommodations roster)
    { to: "/institution/ada-accommodations", icon: Accessibility, label: "ADA Accommodations", tid: "inst-nav-ada", group: "main" },
    // Collapsible group — Audit & Oversight (read-only supervisor/QA layer)
    {
        label: "Audit & Oversight", icon: Scale, group: "main", tid: "inst-nav-oversight-group",
        children: [
            { to: "/institution/oversight/intakes", label: "Intake Overview", tid: "inst-nav-oversight-intakes" },
            { to: "/institution/oversight/booking-release", label: "Booking & Release", tid: "inst-nav-oversight-booking-release" },
            { to: "/institution/oversight/stg-roster", label: "STG Roster", tid: "inst-nav-oversight-stg-roster" },
            { to: "/institution/oversight/unclaimed-property", label: "Unclaimed Property", tid: "inst-nav-oversight-unclaimed-property" },
            { to: "/institution/oversight/clinical-queues", label: "Clinical Queues", tid: "inst-nav-oversight-clinical" },
            { to: "/institution/oversight/reports", label: "Reports Register", tid: "inst-nav-oversight-reports" },
            { to: "/institution/shift-handoff", label: "Shift Handoffs", tid: "inst-nav-oversight-handoffs" },
            { to: "/institution/occupancy", label: "Occupancy (Headcount)", tid: "inst-nav-occupancy" },
        ],
    },
    // Quick access — Messages
    { to: "/institution/messages", icon: MessageSquare, label: "Messages", tid: "inst-nav-messages", group: "main" },
    // Staff Access (supervisor) — surfaced in the header user-menu dropdown
    { to: "/institution/medicaid", icon: Stethoscope, label: "Medicaid Tracker", tid: TIDS.instNavMedicaid, group: "staff" },
    { to: "/institution/record-requests", icon: FileStack, label: "Records Requests", tid: "inst-nav-record-requests", group: "staff", recordsOnly: true },
    { to: "/institution/ada-management", icon: Accessibility, label: "ADA Management", tid: "inst-nav-ada-management", group: "staff", adaOnly: true },
    { to: "/institution/grievances", icon: FileWarning, label: "Grievances", tid: "inst-nav-grievances", group: "staff", grievanceOnly: true },
    { to: "/institution/prea-compliance", icon: ShieldAlert, label: "PREA", tid: "inst-nav-prea", group: "staff", preaOnly: true },
    { to: "/institution/mortality", icon: Skull, label: "Mortality Review", tid: "inst-nav-mortality", group: "staff", mortalityOnly: true },
    { to: "/institution/reports", icon: TrendingUp, label: "Central Reports", tid: "inst-nav-reports", group: "staff" },
    {
        label: "Settings", icon: Settings, group: "staff", tid: "inst-nav-settings-group",
        children: [
            { to: "/institution/clinical-staff", icon: Stethoscope, label: "Clinical Staff", tid: "inst-nav-clinical-staff" },
            { to: "/institution/correctional-staff", icon: Users, label: "Correctional Staff", tid: "inst-nav-correctional-staff" },
            { to: "/institution/settings", icon: Settings, label: "Facility Settings", tid: "inst-nav-settings" },
        ],
    },
    {
        label: "Billing", icon: Receipt, group: "staff", tid: "inst-nav-billing-group",
        children: [
            { to: "/institution/billing", icon: Receipt, label: "Invoices & Billing", tid: "inst-nav-billing" },
            { to: "/institution/superbill", icon: ClipboardList, label: "Superbill", tid: "inst-nav-superbill" },
            { to: "/institution/plan", icon: FileText, label: "Plan & Contract", tid: "inst-nav-plan" },
        ],
    },
];

export default function InstitutionLayout() {
    const { user, logout, fetchMe } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [activeCount, setActiveCount] = useState(0);
    const [ppdOverdueCount, setPpdOverdueCount] = useState(0);
    const [grievanceOpen, setGrievanceOpen] = useState(0);
    const [labAttention, setLabAttention] = useState(0);
    const [housingAlerts, setHousingAlerts] = useState(0);
    const [sickCallOverdue, setSickCallOverdue] = useState(0);
    const [withdrawalOverdue, setWithdrawalOverdue] = useState(0);
    const [referralOverdue, setReferralOverdue] = useState(0);
    const [rescheduleCount, setRescheduleCount] = useState(0);
    const [dentalDue, setDentalDue] = useState(0);
    const [dentalOverdue, setDentalOverdue] = useState(0);
    const [offsiteAttention, setOffsiteAttention] = useState(0);
    const [worklistPastDue, setWorklistPastDue] = useState(0);
    const [autoSchedEnabled, setAutoSchedEnabled] = useState(true);
    const [autoSchedPending, setAutoSchedPending] = useState(0);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [showAssignment, setShowAssignment] = useState(false);
    const unreadMessages = useUnreadMessages();
    const [subscription, setSubscription] = useState(null);
    const [signedLocal, setSignedLocal] = useState(false);
    const [guideOpen, setGuideOpen] = useState(false);
    const [activating, setActivating] = useState(false);

    const activatePlan = async () => {
        setActivating(true);
        try {
            const r = await api.post("/institution/billing/activate");
            setSubscription(r.data.subscription);
        } catch (e) { /* noop */ }
        finally { setActivating(false); }
    };

    // Pending facilities must sign the contract + liability release first
    const needsContract = !signedLocal && user?.institution?.status === "pending" && !user?.institution?.contract_signed;
    // All facility staff must change temp passwords + sign the institution consents
    const needsStaffGate = !!user && (!!user.must_change_password || !user.staff_consent_accepted);

    useEffect(() => {
        if (needsContract || needsStaffGate) return undefined;
        let cancelled = false;
        const fetchActive = async () => {
            try {
                const { data } = await api.get("/institution/dashboard");
                if (!cancelled) { setActiveCount(data.active_alerts || 0); setPpdOverdueCount(data.ppd_overdue_count || 0); }
            } catch (e) { /* noop */ }
        };
        fetchActive();
        const id = setInterval(fetchActive, 15000);
        return () => { cancelled = true; clearInterval(id); };
    }, [needsContract]);

    useEffect(() => {
        if (needsContract || needsStaffGate) return undefined;
        let cancelled = false;
        const fetchGrv = async () => {
            try {
                const { data } = await api.get("/institution/grievances/counts");
                if (!cancelled) setGrievanceOpen(data.grievance_open || 0);
            } catch (e) { /* noop */ }
        };
        fetchGrv();
        const id = setInterval(fetchGrv, 30000);
        return () => { cancelled = true; clearInterval(id); };
    }, [needsContract, needsStaffGate]);

    useEffect(() => {
        if (needsContract || needsStaffGate) return undefined;
        let cancelled = false;
        const fetchLabs = async () => {
            if (hasPerm(user, "care_management")) {
                try {
                    const { data } = await api.get("/institution/lab-attention-count");
                    if (!cancelled) setLabAttention(data.count || 0);
                } catch (e) { /* noop */ }
            }
            try {
                const { data } = await api.get("/institution/housing/alert-count");
                if (!cancelled) setHousingAlerts(data.total || 0);
            } catch (e) { /* noop */ }
            if (hasPerm(user, "care_management")) {
                try {
                    const { data } = await api.get("/institution/sick-call/attention-count");
                    if (!cancelled) setSickCallOverdue(data.overdue || 0);
                } catch (e) { /* noop */ }
            }
            try {
                if (hasPerm(user, "care_management")) {
                    const { data } = await api.get("/institution/withdrawal-monitoring/count");
                    if (!cancelled) setWithdrawalOverdue(data.overdue || 0);
                }
            } catch (e) { /* noop */ }
            try {
                if (hasPerm(user, "care_management")) {
                    const { data } = await api.get("/institution/dental/due-soon-count");
                    if (!cancelled) { setDentalDue(data.count || 0); setDentalOverdue((data.overdue || 0) + (data.referrals_past_due || 0)); }
                }
            } catch (e) { /* noop */ }
            try {
                if (hasPerm(user, "care_management")) {
                    const { data } = await api.get("/institution/offsite-care/attention-count");
                    if (!cancelled) setOffsiteAttention(data.count || 0);
                }
            } catch (e) { /* noop */ }
            try {
                if (hasPerm(user, "care_management")) {
                    const { data } = await api.get("/institution/scheduling-worklist/count");
                    if (!cancelled) setWorklistPastDue(data.count || 0);
                }
            } catch (e) { /* noop */ }
            try {
                if (hasPerm(user, "care_management") || hasPerm(user, "reports")) {
                    const { data } = await api.get("/institution/oversight/intakes/overdue-referral-count");
                    if (!cancelled) setReferralOverdue(data.count || 0);
                }
            } catch (e) { /* noop */ }
            try {
                const { data } = await api.get("/institution/facility-config");
                if (!cancelled) setAutoSchedEnabled(data.auto_scheduling_enabled !== false);
            } catch (e) { /* noop */ }
            if (hasPerm(user, "care_management")) {
                try {
                    const { data } = await api.get("/institution/auto-scheduling/pending-count");
                    if (!cancelled) setAutoSchedPending(data.pending || 0);
                } catch (e) { /* noop */ }
            }
            if (hasPerm(user, "manage_staff")) {
                try {
                    const { data } = await api.get("/institution/reschedule-queue");
                    if (!cancelled) setRescheduleCount(data.count || 0);
                } catch (e) { /* noop */ }
            }
        };
        fetchLabs();
        const id = setInterval(fetchLabs, 30000);
        return () => { cancelled = true; clearInterval(id); };
    }, [needsContract, needsStaffGate, user]);

    useEffect(() => {
        if (needsContract || needsStaffGate) return undefined;
        if (!hasPerm(user, "billing")) return undefined;
        let cancelled = false;
        api.get("/institution/billing/status")
            .then((r) => { if (!cancelled) setSubscription(r.data); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [needsContract, needsStaffGate, user]);

    // First-login "Getting Started" guide (auto-opens once; reopenable via header button)
    useEffect(() => {
        if (needsContract || needsStaffGate) return;
        if (user && !user.has_seen_institution_guide) setGuideOpen(true);
    }, [user, needsContract, needsStaffGate]);

    const closeGuide = async () => {
        setGuideOpen(false);
        if (user && !user.has_seen_institution_guide) {
            try {
                await api.post("/onboarding/institution-guide-seen");
                fetchMe();
            } catch (e) { /* noop */ }
        }
    };

    const handleLogout = async () => {
        await logout();
        navigate("/", { replace: true });
    };

    if (needsContract) {
        return <ContractSigningGate onSigned={() => { setSignedLocal(true); fetchMe(); }} />;
    }

    if (needsStaffGate) {
        return <StaffConsentGate basePath="/institution" needsPassword={!!user.must_change_password} onDone={fetchMe} />;
    }

    // Service-tier gating — each tier only exposes the features it pays for.
    // RBAC gating — invited staff only see nav items their permissions allow.
    const tier = user?.institution?.service_tier || "clinical";
    const prerelease = hasPrerelease(tier);
    const clinical = hasClinical(tier);
    const careOrPre = prerelease || clinical;
    const medicaid = hasMedicaid(user?.institution);
    const isBaseline = !careOrPre;  // Tier 1 — inmate app + safety only
    const FEATURE_OK = {
        // Crisis Dashboard is folded into Custody → Security Rounds for full facilities;
        // baseline (app-only) facilities still use it as their standalone home.
        "Crisis Dashboard": isBaseline,
        // Custody is reachable via the Daily-Operations "Custody" tab — hidden from the sidebar.
        "Custody": false,
        // Clinical Alerts (crisis alerts) folded into Custody → Security Rounds — hidden from the sidebar.
        "Clinical Alerts": false,
        // Court Docket now lives to the right of the Custody tab on the daily-ops hub.
        "Court Docket": false,
        "Appointments": careOrPre,
        "Dashboard": careOrPre,
        "Auto-Scheduling": careOrPre && autoSchedEnabled,
        "Reschedule Queue": careOrPre,
        "Clinical Record": careOrPre,
        "Pre-Release": prerelease,
        "Recidivism Risk": careOrPre,
        "Medicaid Tracker": true,
        "Pharmacies & Labs": clinical,
        "Clinical Staff": careOrPre,
    };
    const PERM_FOR = {
        "Metrics & Reporting": "reports", "Reports": "reports",
        "Invoices & Billing": "billing", "Plan & Contract": "billing", "Superbill": "billing",
        "Pharmacies & Labs": "manage_staff",
        "Clinical Staff": "manage_staff", "Correctional Staff": "manage_staff", "Facility Settings": "manage_staff",
        "Care Requests": "care_management", "Pre-Release": "care_management", "Medicaid Tracker": "reports",
        "Auto-Scheduling": "care_management",
        "Reschedule Queue": "manage_staff",
        "Clinical Record": "care_management",
        "Withdrawal Monitoring": "care_management",
        "Intake Screening": "care_management",
        "eMAR (Med Pass)": "care_management",
        "Infirmary": "care_management",
        "Dental": "care_management",
        "Scheduling Worklist": "care_management",
        "Diet Orders / Kitchen": "care_management",
        "Disciplinary Hearings": ["custody", "care_management"],
        "Court Docket": ["custody", "care_management"],
        "Out-of-Facility Care": "care_management",
        "Infection Control": "care_management",
        "Medication Safety": "care_management",
        "Clinical Incidents": "care_management",
        "Occupancy": ["custody", "care_management"],
        "Custody Reconciliation": ["custody", "care_management"],
        "Use of Force": ["custody", "care_management"],
        "Incident Reports": ["custody", "care_management"],
        "Custody": ["custody", "care_management"],
        "Custody Board": ["custody", "care_management"],
        "Documentation Log": ["custody", "care_management"],
        "Security Rounds": ["custody", "care_management"],
        "Safety Checks": ["custody", "care_management"],
        "Meal Log": ["custody", "care_management"],
        "Shift Handoff": ["custody", "care_management"],
        "Shift HandOff": ["custody", "care_management"],
        "Contraband / Meds": ["custody", "care_management"],
        "Refusal Register": "care_management",
        "Women's Health": "care_management",
        "Clinical Queue": "care_management",
        "Access Codes": "manage_staff", "Bulk Enroll": "manage_staff",
        "Manage Staff & Settings": ["manage_staff", "supervisor_reports"],
    };
    // Facility-role overrides: supervisors may access their own staff-permission
    // settings even without manage_staff.
    const ROLE_OVERRIDE = { "Manage Staff & Settings": "supervisor" };
    const permOk = (label) => {
        const need = PERM_FOR[label];
        const roleOk = ROLE_OVERRIDE[label] && user?.facility_role === ROLE_OVERRIDE[label];
        // eMAR (Med Pass) is visible to care_management OR any nurse/staff granted the
        // Medications (eMAR) chart permission — so eMAR-only staff can pass meds.
        if (label === "eMAR (Med Pass)") {
            const ca = (user?.permissions && typeof user.permissions === "object" && !Array.isArray(user.permissions) && user.permissions.chart_access) || [];
            if (hasPerm(user, "care_management") || (Array.isArray(ca) && ca.includes("medications"))) return true;
        }
        if (!need) return true;
        const ok = Array.isArray(need) ? hasAnyPerm(user, need) : hasPerm(user, need);
        return ok || roleOk;
    };
    const canViewPrea = !user?.invited_by
        || !!(user?.permissions && typeof user.permissions === "object" && !Array.isArray(user.permissions) && user.permissions.prea_coordinator)
        || user?.role === "admin";
    const _perms = (user?.permissions && typeof user.permissions === "object" && !Array.isArray(user.permissions)) ? user.permissions : {};
    const canViewAda = !user?.invited_by || !!_perms.ada_coordinator || !!_perms.manage_staff || user?.role === "admin";
    const canViewGrievance = !user?.invited_by || !!_perms.grievance_coordinator || !!_perms.manage_staff || user?.role === "admin";
    const canViewMortality = !user?.invited_by || !!_perms.mortality_review || !!_perms.manage_staff || user?.role === "admin";
    const canViewRecords = !user?.invited_by || !!_perms.records_request || !!_perms.manage_staff || user?.role === "admin";
    // Supervisor-level compliance reports (Compliance, Regulatory Deadlines, DOJ, CQI) — hidden from
    // facility staff; owners/admins and staff granted the `supervisor_reports` setting can see them.
    const canViewSupervisorReports = !user?.invited_by || !!_perms.supervisor_reports || !!_perms.manage_staff || user?.role === "admin";
    const childOk = (c) =>
        (!c.perm || hasPerm(user, c.perm)) &&
        permOk(c.label) &&
        (!c.preaOnly || canViewPrea) &&
        (!(c.label in FEATURE_OK) || FEATURE_OK[c.label]);
    // Recursively filter nested children (sub-groups keep only visible leaves; dropped if empty).
    const filterChildren = (arr) => arr
        .map((c) => (c.children ? { ...c, children: c.children.filter(childOk) } : c))
        .filter((c) => (c.children ? c.children.length > 0 : childOk(c)));
    const visibleNav = navItems
        .filter((n) =>
            !n.hidden &&
            (!(n.label in FEATURE_OK) || FEATURE_OK[n.label]) &&
            permOk(n.label) &&
            (!n.preaOnly || canViewPrea) &&
            (!n.adaOnly || canViewAda) &&
            (!n.grievanceOnly || canViewGrievance) &&
            (!n.mortalityOnly || canViewMortality) &&
            (!n.recordsOnly || canViewRecords)
        )
        .map((n) => (n.children ? { ...n, children: filterChildren(n.children) } : n))
        .filter((n) => !n.children || n.to || n.children.length > 0);
    let mainNav = visibleNav.filter((n) => n.group === "main");
    // Baseline (app-only) facilities lead with the Crisis Dashboard as their home.
    if (isBaseline) {
        const order = ["Crisis Dashboard"];
        const rank = (l) => { const i = order.indexOf(l); return i === -1 ? 999 : i; };
        mainNav = [...mainNav].sort((a, b) => rank(a.label) - rank(b.label));
    }
    const staffNav = visibleNav.filter((n) => n.group === "staff");
    // Daily Operations hub tab detection (drives active state for the query-param nav items)
    const hubTab = new URLSearchParams(location.search).get("tab") || "appointments";
    const onHub = location.pathname === "/institution";
    // Attention badges keyed by nav label (shown on items and aggregated on collapsed groups)
    const badges = {
        "Crisis Dashboard": activeCount + ppdOverdueCount,
        "Messages": unreadMessages,
        "Grievance & PREA": grievanceOpen,
        "Grievances": grievanceOpen,
        "Clinical Record": labAttention,
        "Dental": dentalDue + dentalOverdue,
        "Out-of-Facility Care": offsiteAttention,
        "Scheduling Worklist": worklistPastDue,
        "Withdrawal Monitoring": withdrawalOverdue,
        "Clinical Queue": sickCallOverdue,
        "Keep-Separate & STG": housingAlerts,
        "Intake Overview": referralOverdue,
        "Auto-Scheduling": autoSchedPending,
        "Reschedule Queue": rescheduleCount,
    };

    // Phase 4C — full lockout when the subscription is unpaid past grace
    if (subscription?.locked) {
        return <SubscriptionLockScreen onLogout={handleLogout} audience="staff" />;
    }

    // Baseline (app-only) facilities have no Appointments dashboard — land on Crisis Dashboard.
    if (isBaseline && location.pathname === "/institution") {
        return <Navigate to="/institution/crisis-dashboard" replace />;
    }

    return (
        <div className="min-h-screen flex flex-col bg-phoenix-bg">
            {/* Top header — logo, Guide, user-menu dropdown (Staff Access) */}
            <header
                data-testid="inst-topbar"
                className="sticky top-0 z-40 bg-white border-b border-border/70 px-4 sm:px-6 py-3 flex items-center gap-3"
            >
                <button
                    onClick={() => setSidebarOpen(true)}
                    data-testid="inst-sidebar-toggle"
                    className="lg:hidden p-2 -ml-2 rounded-lg text-phoenix-navy hover:bg-secondary/60 transition-colors"
                    aria-label="Open menu"
                >
                    <Menu className="w-5 h-5" />
                </button>
                <PhoenixWordmark size={32} />

                <div className="ml-auto flex items-center gap-2 sm:gap-3">
                    {hasPerm(user, "care_management") && <FacilityNotificationBell />}
                    <button
                        onClick={() => setGuideOpen(true)}
                        data-testid="institution-guide-button"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold text-phoenix-navy border border-border hover:bg-secondary/60 transition-colors"
                    >
                        <BookOpen className="w-4 h-4 text-phoenix-orange" />
                        <span className="hidden sm:inline">Guide</span>
                    </button>

                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                data-testid="inst-user-menu"
                                className="flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-secondary/60 transition-colors max-w-[220px]"
                            >
                                <div className="text-right leading-tight hidden sm:block min-w-0">
                                    <div className="text-sm font-semibold text-foreground truncate">
                                        {user?.full_name || user?.email?.split("@")[0]}
                                    </div>
                                    <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                                </div>
                                <span data-testid="inst-user-avatar"
                                    className="sm:hidden w-8 h-8 rounded-full bg-phoenix-navy text-white text-xs font-bold flex items-center justify-center shrink-0">
                                    {((user?.full_name || user?.email || "?").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("") || "?").toUpperCase()}
                                </span>
                                <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-60">
                            <div className="px-2 py-1.5 sm:hidden">
                                <div className="text-sm font-semibold text-foreground truncate">
                                    {user?.full_name || user?.email?.split("@")[0]}
                                </div>
                                <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                            </div>
                            <div className="px-2 pb-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                                <Building2 className="w-3 h-3" />
                                <span className="truncate">{user?.institution?.name || "Facility"}</span>
                            </div>
                            {staffNav.length > 0 && (
                                <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground">
                                        Staff Access
                                    </DropdownMenuLabel>
                                    {staffNav.map((it) => (it.children ? (
                                        <DropdownMenuSub key={it.label}>
                                            <DropdownMenuSubTrigger data-testid={`menu-${it.tid}`} className="gap-2 cursor-pointer">
                                                <it.icon className="w-4 h-4 text-muted-foreground" /> {it.label}
                                            </DropdownMenuSubTrigger>
                                            <DropdownMenuPortal>
                                                <DropdownMenuSubContent>
                                                    {it.children.map(({ to, icon: Icon, label, tid }) => (
                                                        <DropdownMenuItem key={to} data-testid={`menu-${tid}`} onSelect={() => navigate(to)} className="gap-2 cursor-pointer">
                                                            <Icon className="w-4 h-4 text-muted-foreground" /> {label}
                                                        </DropdownMenuItem>
                                                    ))}
                                                </DropdownMenuSubContent>
                                            </DropdownMenuPortal>
                                        </DropdownMenuSub>
                                    ) : (
                                        <DropdownMenuItem
                                            key={it.to}
                                            data-testid={`menu-${it.tid}`}
                                            onSelect={() => navigate(it.to)}
                                            className="gap-2 cursor-pointer"
                                        >
                                            <it.icon className="w-4 h-4 text-muted-foreground" /> {it.label}
                                        </DropdownMenuItem>
                                    )))}
                                </>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                data-testid="menu-my-assignment"
                                onSelect={() => setShowAssignment(true)}
                                className="gap-2 cursor-pointer"
                            >
                                <CalendarClock className="w-4 h-4 text-muted-foreground" /> My Assignment
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                data-testid={TIDS.instLogout}
                                onSelect={handleLogout}
                                className="gap-2 cursor-pointer text-destructive focus:text-destructive"
                            >
                                <LogOut className="w-4 h-4" /> Logout
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </header>

            <div className="flex-1 flex min-h-0">
                {/* Mobile backdrop */}
                {sidebarOpen && (
                    <div
                        data-testid="inst-sidebar-backdrop"
                        className="lg:hidden fixed inset-0 z-40 bg-black/40"
                        onClick={() => setSidebarOpen(false)}
                    />
                )}

                {/* Sidebar — fixed drawer on mobile, static column on lg+ */}
                <aside
                    data-testid="inst-sidebar"
                    className={`fixed lg:static inset-y-0 left-0 z-50 w-64 shrink-0 bg-white border-r border-border/70 flex flex-col transform transition-transform duration-200 ${
                        sidebarOpen ? "translate-x-0" : "-translate-x-full"
                    } lg:translate-x-0`}
                >
                    <div className="lg:hidden px-5 py-4 border-b border-border/70 flex items-center justify-between">
                        <PhoenixWordmark size={28} />
                        <button
                            onClick={() => setSidebarOpen(false)}
                            data-testid="inst-sidebar-close"
                            className="text-muted-foreground hover:text-phoenix-navy p-1"
                            aria-label="Close menu"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
                        {mainNav.map((item) => item.children ? (
                            <NavGroup key={item.label} item={item} badges={badges} onNavigate={() => setSidebarOpen(false)} onHub={onHub} hubTab={hubTab} />
                        ) : (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.exact}
                                data-testid={item.tid}
                                onClick={() => setSidebarOpen(false)}
                                className={({ isActive }) => {
                                    const active = item.tabKey ? (onHub && hubTab === item.tabKey) : isActive;
                                    return `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${item.indent ? "pl-9" : ""} ${
                                        active
                                            ? "bg-phoenix-navy text-white"
                                            : "text-muted-foreground hover:bg-secondary/60 hover:text-phoenix-navy"
                                    }`;
                                }}
                            >
                                <item.icon className="w-4 h-4" />
                                {item.label}
                                <NavBadge label={item.label} badges={badges} />
                            </NavLink>
                        ))}
                    </nav>
                    <div className="px-5 py-4 border-t border-border/70 text-xs text-muted-foreground flex items-center gap-1.5">
                        <Building2 className="w-3 h-3" />
                        <span className="truncate">{user?.institution?.name || "Facility"}</span>
                    </div>
                </aside>

                {/* Main */}
                <main className="flex-1 flex flex-col min-w-0">
                    {hasPerm(user, "care_management") && <PharmacyRejectionBanner />}
                    {subscription?.status === "pilot" && (
                        <div
                            data-testid="pilot-active-banner"
                            className="bg-violet-50 border-b border-violet-200 px-6 py-3 flex items-center gap-3"
                        >
                            <Rocket className="w-5 h-5 text-violet-600 shrink-0" />
                            <div className="text-sm text-violet-800 font-medium">
                                Pilot active — <span className="font-bold">{subscription.pilot?.discount_pct}% off</span>.{" "}
                                <span className="font-bold">{subscription.pilot?.days_left} day{subscription.pilot?.days_left === 1 ? "" : "s"}</span> left
                                {subscription.pilot?.ends_at ? ` (ends ${fmtDate(subscription.pilot.ends_at)})` : ""}.
                            </div>
                        </div>
                    )}
                    {subscription?.status === "pilot_expired" && (
                        <div
                            data-testid="pilot-readonly-banner"
                            className="bg-orange-50 border-b border-orange-300 px-6 py-3 flex items-center gap-3"
                        >
                            <Lock className="w-5 h-5 text-orange-600 shrink-0" />
                            <div className="text-sm text-orange-900 font-medium">
                                Your pilot has ended — the facility is in <span className="font-bold">read-only mode</span>. Sign up to continue using Blue Phoenix.
                            </div>
                            {hasPerm(user, "billing") ? (
                                <button
                                    onClick={activatePlan}
                                    disabled={activating}
                                    data-testid="pilot-activate-btn"
                                    className="ml-auto px-3 py-1.5 bg-orange-600 text-white rounded-md text-xs font-semibold hover:bg-orange-700 transition-colors shrink-0 disabled:opacity-60"
                                >
                                    {activating ? "Activating…" : "Sign up to continue →"}
                                </button>
                            ) : (
                                <span className="ml-auto text-xs text-orange-800 shrink-0">Ask your administrator to activate a plan.</span>
                            )}
                        </div>
                    )}
                    {subscription?.status === "unpaid" && (
                        <div
                            data-testid="subscription-unpaid-banner"
                            className="bg-amber-50 border-b border-amber-200 px-6 py-3 flex items-center gap-3"
                        >
                            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                            <div className="text-sm text-amber-800 font-medium">
                                Your Blue Phoenix subscription is unpaid (invoice {subscription.invoice_number}). Please pay within{" "}
                                <span className="font-bold">{subscription.days_until_lock} day{subscription.days_until_lock === 1 ? "" : "s"}</span>{" "}
                                or facility access will be suspended.
                            </div>
                            <NavLink
                                to="/institution/billing"
                                className="ml-auto px-3 py-1 bg-amber-600 text-white rounded-md text-xs font-semibold hover:bg-amber-700 transition-colors shrink-0"
                            >
                                View billing →
                            </NavLink>
                        </div>
                    )}
                    {activeCount > 0 && (
                        <div
                            data-testid={TIDS.instActiveBanner}
                            className="bg-destructive text-white px-6 py-3 flex items-center gap-3 shadow-sm"
                        >
                            <AlertTriangle className="w-5 h-5 shrink-0 animate-pulse" />
                            <div className="text-sm font-medium">
                                {activeCount} active crisis {activeCount === 1 ? "alert" : "alerts"} requiring attention
                            </div>
                            <NavLink
                                to="/institution/crisis-dashboard"
                                className="ml-auto px-3 py-1 bg-white/15 hover:bg-white/25 rounded-md text-xs font-semibold transition-colors"
                            >
                                Review now →
                            </NavLink>
                        </div>
                    )}
                    {hasPerm(user, "care_management") && ppdOverdueCount > 0 && (
                        <div
                            data-testid="inst-ppd-overdue-banner"
                            className="bg-red-700 text-white px-6 py-3 flex items-center gap-3 shadow-sm"
                        >
                            <Syringe className="w-5 h-5 shrink-0" />
                            <div className="text-sm font-medium">
                                {ppdOverdueCount} PPD{ppdOverdueCount === 1 ? "" : "s"} unread past the 72-hour window — no longer a reliable result
                            </div>
                            <NavLink
                                to="/institution/crisis-dashboard"
                                className="ml-auto px-3 py-1 bg-white/15 hover:bg-white/25 rounded-md text-xs font-semibold transition-colors"
                            >
                                Review now →
                            </NavLink>
                        </div>
                    )}
                    <div className="flex-1 overflow-auto">
                        <Suspense fallback={<PageLoader />}>
                            <Outlet />
                        </Suspense>
                    </div>
                </main>
            </div>
            <InstitutionGuideModal open={guideOpen} onClose={closeGuide} />
            <LoginAssignmentPopup />
            <Dialog open={showAssignment} onOpenChange={setShowAssignment}>
                <DialogContent data-testid="my-assignment-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>My Assignment</DialogTitle></DialogHeader>
                    <MyAssignmentPanel />
                </DialogContent>
            </Dialog>
        </div>
    );
}

function NavBadge({ label, badges }) {
    const count = (badges && badges[label]) || 0;
    if (count <= 0) return null;
    const styleMap = {
        "Crisis Dashboard": "bg-destructive",
        "Messages": "bg-phoenix-orange",
        "Grievance & PREA": "bg-phoenix-orange",
        "Grievances": "bg-phoenix-orange",
        "Pharmacy Orders": "bg-red-600",
        "Clinical Queue": "bg-red-600",
        "Reschedule Queue": "bg-red-600",
    };
    const tidMap = {
        "Messages": "inst-messages-badge",
        "Grievance & PREA": "inst-grievance-badge",
        "Grievances": "inst-grievance-badge",
        "Pharmacy Orders": "inst-pharmacy-badge",
        "Clinical Queue": "inst-clinical-queue-badge",
        "Reschedule Queue": "inst-reschedule-badge",
    };
    return (
        <span
            data-testid={tidMap[label]}
            className={`ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-white text-[11px] font-bold ${styleMap[label] || "bg-phoenix-orange"}`}
        >
            {count}
        </span>
    );
}

function NavGroup({ item, badges, onNavigate, onHub, hubTab }) {
    const location = useLocation();
    const Icon = item.icon;
    const children = item.children || [];
    const hasChildren = children.length > 0;
    const isChildActive = (c) =>
        c.children ? c.children.some(isChildActive)
            : c.tabKey ? (onHub && hubTab === c.tabKey)
                : (location.pathname === c.to || (!c.exact && location.pathname.startsWith(c.to + "/")));
    const childActive = children.some(isChildActive);
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 256 });
    // Aggregate attention count surfaced on the group header (recurses into sub-groups)
    const countFor = (c) => c.children
        ? c.children.reduce((s, x) => s + ((badges && badges[x.label]) || 0), 0)
        : ((badges && badges[c.label]) || 0);
    const groupCount = children.reduce((sum, c) => sum + countFor(c), 0);

    useEffect(() => { setOpen(false); }, [location.pathname]);
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    const toggle = (e) => {
        if (open) { setOpen(false); return; }
        const r = e.currentTarget.getBoundingClientRect();
        const ar = e.currentTarget.closest("aside")?.getBoundingClientRect();
        setPos({ top: Math.max(8, Math.min(r.top, window.innerHeight - 360)), left: (ar?.right ?? 256) + 4 });
        setOpen(true);
    };

    return (
        <div>
            <button
                data-testid={`${item.tid}-toggle`}
                onClick={toggle}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    open || childActive ? "bg-secondary/70 text-phoenix-navy" : "text-muted-foreground hover:bg-secondary/60 hover:text-phoenix-navy"
                }`}
            >
                <Icon className="w-4 h-4" />
                <span className="flex-1 text-left">{item.label}</span>
                {groupCount > 0 && (
                    <span
                        data-testid={`${item.tid}-badge`}
                        className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-phoenix-orange text-white text-[11px] font-bold"
                    >
                        {groupCount}
                    </span>
                )}
                <ChevronRight className={`w-4 h-4 transition-transform ${open ? "rotate-90" : ""}`} />
            </button>

            {open && hasChildren && (
                <>
                    <div className="fixed inset-0 z-[55]" onClick={() => setOpen(false)} data-testid={`${item.tid}-flyout-backdrop`} />
                    <div
                        data-testid={`${item.tid}-flyout`}
                        style={{ top: pos.top, left: pos.left }}
                        className="fixed z-[60] w-60 max-h-[80vh] overflow-y-auto bg-white border border-border/70 rounded-xl shadow-xl p-2 space-y-1"
                    >
                        <div className="px-2 py-1.5 text-[10px] font-bold text-muted-foreground uppercase tracking-wider">{item.label}</div>
                        {children.map((c) => (
                            c.children ? (
                                <NavSubGroup
                                    key={c.label}
                                    item={c}
                                    badges={badges}
                                    onHub={onHub}
                                    hubTab={hubTab}
                                    onNavigate={() => { setOpen(false); onNavigate?.(); }}
                                />
                            ) : (
                                <NavLink
                                    key={c.to}
                                    to={c.to}
                                    end={c.exact}
                                    data-testid={c.tid}
                                    onClick={() => { setOpen(false); onNavigate?.(); }}
                                    className={({ isActive }) => {
                                        const active = c.tabKey ? (onHub && hubTab === c.tabKey) : isActive;
                                        return `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                            active ? "bg-phoenix-navy text-white" : "text-muted-foreground hover:bg-secondary/60 hover:text-phoenix-navy"
                                        }`;
                                    }}
                                >
                                    {c.label}
                                    <NavBadge label={c.label} badges={badges} />
                                </NavLink>
                            )
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}


// Nested sub-group rendered INSIDE a NavGroup flyout (e.g. Clinical Care → Programs).
// Inline-expandable list of leaf links; no further nesting.
function NavSubGroup({ item, badges, onNavigate, onHub, hubTab }) {
    const location = useLocation();
    const children = item.children || [];
    const isChildActive = (c) =>
        c.tabKey ? (onHub && hubTab === c.tabKey)
            : (location.pathname === c.to || (!c.exact && location.pathname.startsWith(c.to + "/")));
    const childActive = children.some(isChildActive);
    const [open, setOpen] = useState(childActive);
    const subCount = children.reduce((s, c) => s + ((badges && badges[c.label]) || 0), 0);

    return (
        <div>
            <button
                data-testid={`${item.tid}-toggle`}
                onClick={() => setOpen((o) => !o)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    open || childActive ? "bg-secondary/70 text-phoenix-navy" : "text-muted-foreground hover:bg-secondary/60 hover:text-phoenix-navy"
                }`}
            >
                <span className="flex-1 text-left">{item.label}</span>
                {subCount > 0 && (
                    <span
                        data-testid={`${item.tid}-badge`}
                        className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-phoenix-orange text-white text-[11px] font-bold"
                    >
                        {subCount}
                    </span>
                )}
                <ChevronRight className={`w-4 h-4 transition-transform ${open ? "rotate-90" : ""}`} />
            </button>
            {open && (
                <div className="mt-1 ml-2 pl-2 border-l border-border/60 space-y-1">
                    {children.map((c) => (
                        <NavLink
                            key={c.to}
                            to={c.to}
                            end={c.exact}
                            data-testid={c.tid}
                            onClick={() => { onNavigate?.(); }}
                            className={({ isActive }) => {
                                const active = c.tabKey ? (onHub && hubTab === c.tabKey) : isActive;
                                return `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                    active ? "bg-phoenix-navy text-white" : "text-muted-foreground hover:bg-secondary/60 hover:text-phoenix-navy"
                                }`;
                            }}
                        >
                            {c.label}
                            <NavBadge label={c.label} badges={badges} />
                        </NavLink>
                    ))}
                </div>
            )}
        </div>
    );
}
